import type { CivilState } from "@peepel/core/drizzle/enums";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import {
	getApiClient,
	getSessionCookieHeader,
	getSessionToken,
} from "~/utils/api-client";

// Cheap presence check used to skip a /me round-trip on /login when there's
// no session cookie. Not load-bearing — /me would still 401 cleanly — just
// avoids the noise.
export const hasSessionToken = createServerFn({ method: "GET" }).handler(
	() => getSessionToken() !== undefined,
);

export const AUTH_KEYS = {
	all: ["auth"] as const,
	me: () => [...AUTH_KEYS.all, "me"] as const,
};

/**
 * Fetch user context
 */
const meQueryOptions = queryOptions({
	queryKey: AUTH_KEYS.me(),
	queryFn: () => fetchMe(),
	// Cache user context for 5 minutes - avoid refetching on every navigation
	staleTime: 5 * 60 * 1000,
	// Keep in cache even when unused (user might navigate away and back)
	gcTime: 10 * 60 * 1000,
});

type OrgDetails = {
	avatar_url: string | null;
	current_role: string | null;
	email: string;
	first_name: string;
	last_name: string;
	person_id: string;
	preferred_language: string;
	national_id_number: string | null;
	bank_account_iban: string | null;
	bank_account_bic: string | null;
	nationality_country_code: string;
	gender: "male" | "female" | "non_binary" | "undisclosed";
	civil_state: CivilState | null;
	place_of_birth: string | null;
	place_of_birth_country_code: string | null;
	date_of_birth: string | null;
	phone: string | null;
	email_personal: string | null;
	emergency_contact_name: string | null;
	emergency_contact_relation: "family" | "friend" | "partner" | null;
	emergency_contact_phone: string | null;
	address_line_1: string | null;
	address_line_2: string | null;
	address_street_name: string | null;
	address_street_number: string | null;
	address_zipcode: string | null;
	address_city: string | null;
	address_state: string | null;
	address_country_code: string | null;
	dependent_children: number;
	dependent_adults_below_65: number;
	dependent_adults_above_65: number;
	dependent_disabled_children: number;
	dependent_disabled_adults_below_65: number;
	dependent_disabled_adults_above_65: number;
	dependent_adults_needing_care_above_65: number;
	spouse_has_income: "yes" | "no" | "unknown";
	spouse_income_situation:
		| "personal_income"
		| "no_income"
		| "limited_income_pension"
		| "limited_income_other_than_pension"
		| null;
	spouse_disabled: boolean | null;
	onboarding_hint_employment_type?: "payroll" | "freelancer";
	/**
	 * The payroll provider has not created this person yet, so nothing Peepel
	 * sends on their behalf can reach payroll and the API refuses payroll-bound
	 * writes. Derived server-side from the same predicate the guard enforces —
	 * see `personPendingAtProvider`.
	 */
	pending_at_provider: boolean;
	/**
	 * Their own fields the payroll provider keeps: it imports them and takes no
	 * edit back. Resolved server-side from the payroll policy.
	 */
	locked_person_fields: string[];
};

type OrgSettings = {
	calendar_checklist: boolean;
	ask_hr: boolean;
	contracts: boolean;
	surveys: boolean;
	expenses: boolean;
	training: boolean;
	performance_reviews: boolean;
	who_is_who: boolean;
	document_notifications: boolean;
	block_personal_edit_selfservice: boolean;
	block_leave_requests_selfservice: boolean;
	block_expenses_selfservice: boolean;
	who_is_who_show_birthdays: boolean;
};

type Organization = {
	org_id: string;
	name: string;
	person_id: number;
	logo_url: string | null;
	is_sensitive: boolean;
	/**
	 * IANA zone the org's calendar runs on.
	 *
	 * Only for instants whose *calendar day* is decided by the org rather than
	 * the reader — a contract expiry is the org's deadline, so it names the same
	 * day for every viewer. Plain timestamps stay in the viewer's zone.
	 */
	timezone: string;
	created_at: string;
	calendar_opens_at: string;
	settings: OrgSettings;
	partner_id: number | null;
	partner_name: string | null;
};

// Server function to fetch user context (organizations, active org, person details)
const fetchMe = createServerFn({ method: "GET" }).handler(async () => {
	const api_client = getApiClient(getSessionCookieHeader());

	const meResult = await api_client.self.api.me.$get();

	if (!meResult.ok) {
		throw new Error("Failed to fetch user context");
	}

	return (await meResult.json()) as {
		organizations: Organization[];
		active_org_id: string | null;
		active_person: OrgDetails | null;
		login_email: string | null;
		payroll_provider: string | null;
	};
});

export { meQueryOptions };
